import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Users, UserCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { roleService } from '../services/roleService';
import { Role, CreateRoleForm, UpdateRoleForm } from '../types';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import RoleForm from '../components/forms/RoleForm';
import DataGrid from '../components/grid/DataGrid';

const RolesPage: React.FC = () => {
  const queryClient = useQueryClient();
  
  // State
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // Queries
  const {
    data: rolesResponse,
    isLoading,
    error
  } = useQuery({
    queryKey: ['roles'],
    queryFn: roleService.getRoles,
  });

  // Mutations
  const createRoleMutation = useMutation({
    mutationFn: (data: CreateRoleForm) => roleService.createRole(data),
    onSuccess: (response) => {
      if (response.success && response.data) {
        toast.success(`Role "${response.data.name}" created successfully!`);
        setShowCreateModal(false);
        queryClient.invalidateQueries({ queryKey: ['roles'] });
      } else {
        toast.error(response.message || 'Failed to create role');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.join(', ') || 'Error creating role';
      toast.error(errorMessage);
    }
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRoleForm }) => roleService.updateRole(id, data),
    onSuccess: (response) => {
      if (response.success && response.data) {
        toast.success(`Role "${response.data.name}" updated successfully!`);
        setShowEditModal(false);
        setSelectedRole(null);
        queryClient.invalidateQueries({ queryKey: ['roles'] });
      } else {
        toast.error(response.message || 'Failed to update role');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.join(', ') || 'Error updating role';
      toast.error(errorMessage);
    }
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: number) => roleService.deleteRole(id),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(`Role "${selectedRole?.name}" deleted successfully!`);
        setShowDeleteDialog(false);
        setSelectedRole(null);
        queryClient.invalidateQueries({ queryKey: ['roles'] });
      } else {
        toast.error(response.message || 'Failed to delete role');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Error deleting role';
      toast.error(errorMessage);
    }
  });

  // Handlers
  const handleCreate = (data: CreateRoleForm) => {
    createRoleMutation.mutate(data);
  };

  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    setShowEditModal(true);
  };

  const handleUpdate = (data: UpdateRoleForm) => {
    if (selectedRole) {
      updateRoleMutation.mutate({ id: selectedRole.id, data });
    }
  };

  const handleDelete = (role: Role) => {
    setSelectedRole(role);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (selectedRole) {
      deleteRoleMutation.mutate(selectedRole.id);
    }
  };

  // Grid columns
  const columns = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (value: string) => (
        <div className="font-medium text-gray-900 dark:text-gray-100">{value}</div>
      )
    },
    {
      id: 'description',
      header: 'Description',
      sortable: true,
      cell: (value: string) => (
        <div className="text-gray-600 dark:text-gray-300">{value || '-'}</div>
      )
    },
    {
      id: 'userCount',
      header: 'Users',
      sortable: true,
      cell: (value: number, row: Role) => (
        <div className="relative group">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <div className="flex items-center text-gray-600 dark:text-gray-300">
              <Users className="h-4 w-4 mr-1" />
              {value || 0}
            </div>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
            <button 
              onClick={() => handleEdit(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit Role"
            >
              <Edit className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => handleDelete(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
              title="Delete Role"
              disabled={!!(row.userCount && row.userCount > 0)}
            >
              <Trash2 className="h-4 w-4"/>
            </button>
          </div>
        </div>
      )
    }
  ], []);

  const roles: Role[] = rolesResponse?.data || [];

  // Search functionality
  const handleSearch = (term: string) => {
    setSearch(term);
  };

  const filteredRoles = useMemo(() => {
    if (!search) return roles;
    return roles.filter(role => 
      role.name.toLowerCase().includes(search.toLowerCase()) ||
      role.description?.toLowerCase().includes(search.toLowerCase())
    );
  }, [roles, search]);

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Roles</h1>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="text-center">
            <div className="text-red-600 dark:text-red-400 text-lg font-medium">Error loading roles</div>
            <div className="text-gray-500 dark:text-gray-400 mt-2">Please try again</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <UserCheck className="h-8 w-8 text-gray-600 dark:text-gray-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Roles</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage user roles and permissions</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Role</span>
        </button>
      </div>

      <DataGrid
        data={filteredRoles}
        columns={columns}
        isLoading={isLoading}
        searchTerm={search}
        onSearch={handleSearch}
        currentPage={1}
        pageSize={filteredRoles.length || 10}
        totalCount={filteredRoles.length}
        totalPages={1}
        onPageChange={() => {}}
        emptyMessage="No roles found"
        getRowClassName={() => 'group'}
        searchPlaceholder="Search roles by name or description..."
        onRowClick={(row) => handleEdit(row as Role)}
        rowTooltip="Edit Role"
      />

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Role"
        maxWidth="md"
      >
        <RoleForm
          onSubmit={handleCreate}
          isLoading={createRoleMutation.isPending}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedRole(null);
        }}
        title="Edit Role"
        maxWidth="md"
      >
        <RoleForm
          role={selectedRole}
          onSubmit={handleUpdate}
          isLoading={updateRoleMutation.isPending}
        />
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedRole(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${selectedRole?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteRoleMutation.isPending}
      />
    </div>
  );
};

export default RolesPage;
